// ============================================================================
// world.js — the central game state. One object graph that every system reads
// from and writes to. Holds the pools, the spatial hash, the player, morale,
// bastions, warlords, counters and the event bus.
//
// The `unit` object shape defined here is THE contract for combat, AI and the
// instanced crowd renderer. It is monomorphic (same fields, same order, always
// initialised) so V8 keeps it fast, and it is pooled so the loop never GCs.
// ============================================================================

import { Pool } from '../core/pool.js';
import { SpatialHash } from '../core/spatialHash.js';
import { Colliders } from './colliders.js';
import { EventBus, EV } from '../core/events.js';
import { makeRng } from '../core/math.js';
import {
  MAX_ENEMIES, MAX_HITSPARKS, MAX_DMGNUM, MAX_PICKUPS, MAX_PROJECTILES, HASH_CELL,
  UNIT_STATS, ST_IDLE, ST_DEAD, PLAYER_HP_MAX, TITAN_MAX,
  MORALE_START, TEAM_PLAYER, TEAM_MAGMAR, UNIT_WARLORD, UNIT_CAPTAIN, TARGET_ONSCREEN,
} from '../core/constants.js';

function makeUnit() {
  return {
    _slot: -1, _active: false, _ai: -1,
    // identity
    team: 0, type: 0, id: 0,
    // transform (x,z ground; y vertical for launches)
    x: 0, z: 0, y: 0,
    px: 0, pz: 0, py: 0,           // previous tick position (render interpolation)
    heading: 0, renderHeading: 0,
    vx: 0, vz: 0, vy: 0,
    // stats
    hp: 1, maxHp: 1, mass: 1, speed: 1,
    atkRange: 1, atkDmg: 1, atkCd: 1, aggro: 1, scale: 1,
    // behaviour state machine
    state: ST_IDLE, stateT: 0,
    atkTimer: 0, atkPhase: 0, swungThisPhase: false, windup: 0,
    guard: 0, guardMax: 0,        // officer block meter (0 for grunts)
    blockCd: 0,
    // ai
    lod: 0, targetSlot: -1, targetIsPlayer: false,
    marchX: 0, marchZ: 0,          // positional march target (targetSlot === -2)
    aiTimer: 0, wanderH: 0, decideT: 0,
    regenT: 0,                     // leaders: time since last damage (for slow regen)
    // reactions / fake ragdoll juggle
    tumble: 0, tumbleRate: 0,
    // instancing / animation
    instId: -1, animState: 0, animT: 0, animPhase: 0,
    flash: 0, fade: 1,
    hitToken: -1,                 // last swing/shockwave that hit this unit (multi-hit guard)
    // anchoring
    homeX: 0, homeZ: 0, bastionId: -1,
    // named-officer descriptor (null for grunts)
    warlord: null,
    engaged: false,
    _barkLow: false,              // barks.js: has this general already cried for backup?
  };
}

function makeSpark() {
  return { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, life: 0, maxLife: 1, size: 1, kind: 0 };
}
function makeDmgNum() {
  return { x: 0, y: 0, z: 0, vy: 0, value: 0, life: 0, maxLife: 1, kind: 0 };
}
function makePickup() {
  return { x: 0, z: 0, y: 0, kind: 0, value: 0, life: 0, bob: 0 };
}
function makeProjectile() {
  return { x: 0, y: 0, z: 0, vx: 0, vy: 0, vz: 0, team: 0, dmg: 0, life: 0, spin: 0 };
}

export class World {
  constructor(seed = 1337) {
    this.rng = makeRng(seed);
    this.events = new EventBus();
    this.EV = EV;

    this.units = new Pool(MAX_ENEMIES, makeUnit);
    this.sparks = new Pool(MAX_HITSPARKS, makeSpark);
    this.dmgnums = new Pool(MAX_DMGNUM, makeDmgNum);
    this.pickups = new Pool(MAX_PICKUPS, makePickup);
    this.projectiles = new Pool(MAX_PROJECTILES, makeProjectile);

    this.hash = new SpatialHash(HASH_CELL);
    this.colliders = new Colliders();   // static arena obstacles (rebuilt per level)

    this.player = this._makePlayer();

    this.bastions = [];      // filled by battlefield.js
    this.warlords = [];       // filled by warlords.js (subset of units, flagged)
    this.warmaster = null;    // allied leader unit (defeat if it falls)
    this.dynast = null;       // magmar leader unit (victory if slain)
    this.engagedWarlord = null; // warlord currently shown on the HUD boss bar
    this.gates = [];          // filled by battlefield.js
    this.battlefield = null;  // set by main; spawns.js uses it to recolour/break

    this.morale = MORALE_START;         // 0..100, >50 favours the player
    this.killCount = 0;                 // enemies slain by the player
    this.killByTeam = [0, 0];           // total losses per team
    this.nextMilestone = 100;
    this.warlordsSlain = 0;

    // global combat feel state
    this.hitstop = 0;      // seconds of frozen logic remaining
    this.shake = 0;        // camera shake energy 0..1+
    this.combo = 0;        // current hit counter
    this.comboTimer = 0;   // time since last hit
    this.maxCombo = 0;     // best combo this battle (for results)
    this.slowmo = 1;       // time-scale multiplier for render dt effects

    this.gameOver = false;
    this.won = false;
    this.simTime = 0;

    // ---- campaign / level state ----
    this.level = null;          // current level definition (campaign.js)
    this.levelTime = 0;         // seconds elapsed this battle
    this.objectives = [];       // active objective trackers { type, ...state, done }
    this.mods = { enemyHp: 1, enemyDmg: 1, spawn: 1, magmarNear: TARGET_ONSCREEN, allyNear: 135 };
    this.generals = [];         // allied named generals (UNIT_WARLORD on TEAM_PLAYER)
    this.outcome = null;        // 'win' | 'lose' when the battle ends
    this.failReason = '';
    this.peaceful = false;      // free-roam ("walk your lands") — no enemy spawns

    this.nextToken = 0;        // unique token per attack swing / shockwave (multi-hit guard)
    this.shockwaves = [];      // active expanding rings (finisher waves, titanfall pulses)

    this._nextId = 1;
    this._uid = 0;
  }

  _makePlayer() {
    return {
      x: 0, z: 0, y: 0, px: 0, pz: 0, py: 0,
      heading: 0, renderHeading: 0,
      vx: 0, vz: 0, vy: 0,
      hp: PLAYER_HP_MAX, maxHp: PLAYER_HP_MAX,
      titan: 0, titanMax: TITAN_MAX, titanReady: false,
      // control / combat state is owned by combat/player.js; stored here so
      // AI, camera, UI and FX can all read it.
      grounded: true,
      state: 'idle',        // idle | run | attack | dodge | block | hurt | titanfall | dead
      attackNode: null,
      attackT: 0,
      invuln: 0,            // i-frame seconds remaining
      blocking: false,
      guardBroken: 0,
      titanfallT: 0,
      dodgeT: 0, dodgeCd: 0, dodgeDirX: 0, dodgeDirZ: 1,
      comboIndex: 0,        // position in the current normal chain
      moveX: 0, moveZ: 0,   // desired move input (world space)
      sprinting: false,
      radius: 0.6,
      deaths: 0,
    };
  }

  // ---- unit lifecycle ---------------------------------------------------

  /** Allocate & fully initialise a combat unit. Returns null if pool full. */
  spawnUnit(team, type, x, z) {
    const u = this.units.spawn();
    if (!u) return null;
    const s = UNIT_STATS[type];
    u.team = team; u.type = type; u.id = this._nextId++;
    u.x = x; u.z = z; u.y = 0;
    u.px = x; u.pz = z; u.py = 0;
    u.vx = 0; u.vz = 0; u.vy = 0;
    u.heading = this.rng() * Math.PI * 2;
    u.renderHeading = u.heading;
    // Magmar (enemy) stats scale with the level's difficulty mods.
    const foe = team === TEAM_MAGMAR;
    const hpMul = foe ? this.mods.enemyHp : 1;
    const dmgMul = foe ? this.mods.enemyDmg : 1;
    u.hp = s.hp * hpMul; u.maxHp = u.hp; u.mass = s.mass; u.speed = s.speed;
    u.atkRange = s.atkRange; u.atkDmg = s.atkDmg * dmgMul; u.atkCd = s.atkCd;
    u.aggro = s.aggro; u.scale = s.scale;
    u.state = ST_IDLE; u.stateT = 0;
    u.atkTimer = this.rng() * s.atkCd; u.atkPhase = 0; u.swungThisPhase = false; u.windup = 0;
    u.guardMax = type === UNIT_WARLORD ? 80 : type === UNIT_CAPTAIN ? 45 : 0;
    u.guard = u.guardMax; u.blockCd = 0;
    u.lod = 2; u.targetSlot = -1; u.targetIsPlayer = false;
    u.marchX = x; u.marchZ = z; u.regenT = 0;
    u.aiTimer = this.rng() * 0.3; u.decideT = 0; u.wanderH = u.heading;
    u.tumble = 0; u.tumbleRate = 0;
    u.instId = -1; u.animState = 0; u.animT = this.rng() * 4; u.animPhase = this.rng();
    u.flash = 0; u.fade = 1; u.hitToken = -1;
    u.homeX = x; u.homeZ = z; u.bastionId = -1;
    u.warlord = null; u.engaged = false;
    u._barkLow = false;           // clear any stale "call for backup" flag from a recycled slot
    return u;
  }

  /** Mark a unit dead (enters death state; renderer fades then despawns). */
  killUnit(u, byPlayer) {
    if (u.state === ST_DEAD) return;
    const loser = u.team;
    u.state = ST_DEAD; u.stateT = 0; u.hp = 0;
    u.vx *= 0.3; u.vz *= 0.3;
    this.killByTeam[loser]++;
    if (byPlayer && loser === TEAM_MAGMAR) {
      this.killCount++;
      if (this.killCount >= this.nextMilestone) {
        this.events.emit(EV.MILESTONE, { total: this.nextMilestone });
        this.nextMilestone += 100;
      }
    }
    this.events.emit(EV.KILL, {
      x: u.x, z: u.z, team: loser, type: u.type, byPlayer: !!byPlayer, unit: u,
    });
  }

  // ---- FX spawners (pooled, zero-alloc) ---------------------------------

  spawnSpark(x, y, z, vx, vy, vz, life, size, kind) {
    const s = this.sparks.spawn();
    if (!s) return null;
    s.x = x; s.y = y; s.z = z; s.vx = vx; s.vy = vy; s.vz = vz;
    s.life = life; s.maxLife = life; s.size = size; s.kind = kind || 0;
    return s;
  }

  spawnDmgNum(x, y, z, value, kind) {
    const d = this.dmgnums.spawn();
    if (!d) return null;
    d.x = x; d.y = y; d.z = z; d.vy = 2.4; d.value = value | 0;
    d.life = 0.9; d.maxLife = 0.9; d.kind = kind || 0;
    return d;
  }

  spawnProjectile(x, y, z, vx, vy, vz, team, dmg, life) {
    const p = this.projectiles.spawn();
    if (!p) return null;
    p.x = x; p.y = y; p.z = z; p.vx = vx; p.vy = vy; p.vz = vz;
    p.team = team; p.dmg = dmg; p.life = life; p.spin = this.rng() * 6.28;
    return p;
  }

  spawnPickup(x, z, kind, value) {
    const p = this.pickups.spawn();
    if (!p) return null;
    p.x = x; p.z = z; p.y = 0.6; p.kind = kind; p.value = value;
    p.life = 30; p.bob = this.rng() * 6.28;
    return p;
  }

  reset() {
    // Reset for a fresh level. Bastions are geometry-linked (Battlefield owns the
    // meshes/banners), so we keep the array and only reset ownership elsewhere.
    this.units.clear();
    this.sparks.clear();
    this.dmgnums.clear();
    this.pickups.clear();
    this.projectiles.clear();
    this.shockwaves.length = 0;
    this.warlords.length = 0;
    this.generals.length = 0;
    this.dynast = null; this.warmaster = null; this.engagedWarlord = null;
    this.morale = MORALE_START;
    this.killCount = 0;
    this.killByTeam[0] = 0; this.killByTeam[1] = 0;
    this.nextMilestone = 100;
    this.warlordsSlain = 0;
    this.gameOver = false; this.won = false;
    this.outcome = null; this.failReason = '';
    this.peaceful = false;
    this.objectives.length = 0;
    this.levelTime = 0; this.simTime = 0;
    this.combo = 0; this.comboTimer = 0; this.maxCombo = 0;
    // clear transient combat-feel state so a battle that ended on a heavy blow
    // can't carry residual hit-stop / shake into the next battle's opening
    this.hitstop = 0; this.shake = 0; this.slowmo = 1;
    this.mods = { enemyHp: 1, enemyDmg: 1, spawn: 1, magmarNear: TARGET_ONSCREEN, allyNear: 135 };
    Object.assign(this.player, this._makePlayer());
  }
}
